// Core gameplay physics: state creation, input integration, movement,
// enemy AI, power-ups, collision, stage progression.
// Rendering is separated in rendering.js.

import { withSeededMathRandom } from "@/lib/seededRng";
import { aabb, buildLevel, COLORS, SCORE_PER_STAGE, stageFromScore, W, H } from "@/game/levels";

const rand = (a, b) => a + Math.random() * (b - a);
const irand = (a, b) => Math.floor(rand(a, b + 1));

// -------- Collision helpers --------
export function collidesSolid(rect, solids) {
  for (const s of solids) {
    if (aabb(rect, s)) return true;
  }
  return false;
}

export function isInExit(rect, exits) {
  for (const e of exits) {
    if (aabb(rect, e)) return true;
  }
  return false;
}

// Move a rectangle with axis-separated collision against solids
function moveRect(rect, dx, dy, solids) {
  let moved = { x: rect.x, y: rect.y };
  moved.x += dx;
  if (collidesSolid({ ...rect, x: moved.x }, solids)) {
    moved.x = rect.x;
  }
  moved.y += dy;
  if (collidesSolid({ ...rect, x: moved.x, y: moved.y }, solids)) {
    moved.y = rect.y;
  }
  return moved;
}

// -------- Stage progression multipliers --------
function stageSpeedMult(stage) {
  return 1 + (stage - 1) * 0.15; // +15% enemy speed per stage
}
function stageEnemyCap(state, stage) {
  const base = state.level === "underground" ? 8 : 6;
  return base + (stage - 1);
}
function stageSpawnInterval(stage, time) {
  const base = Math.max(1.4, 7 - time * 0.03);
  return Math.max(1.2, base - (stage - 1) * 0.6);
}

// -------- Spawners --------
function spawnEnemy(state) {
  const s = state.enemySpawns[irand(0, state.enemySpawns.length - 1)];
  const stageMult = stageSpeedMult(state.stage);

  // Highway: lane-locked traffic.
  if (state.level === "highway") {
    const base = 165;
    const speedJitter = Math.random() * 30;
    const enemy = {
      x: s.x,
      y: s.y - 12,
      w: 46,
      h: 22,
      baseSpeed: base + speedJitter,
      speed: (base + speedJitter) * stageMult,
      angle: s.dir === 1 ? 0 : Math.PI,
      color: COLORS.enemy,
      highwayDir: s.dir,
      lane: s.lane,
    };
    state.enemies.push(enemy);
    return;
  }

  // Beach BOSS: rare (10%) beach buggy that ignores palm walls, wider body, orange.
  // Only spawns from stage 2 onwards, and never more than 1 active at a time.
  if (state.level === "beach") {
    const hasBuggy = state.enemies.some((e) => e.buggy);
    if (!hasBuggy && state.stage >= 2 && Math.random() < 0.1) {
      const base = 130;
      const enemy = {
        x: s.x, y: s.y,
        w: 44, h: 26,
        baseSpeed: base + state.difficulty * 5,
        speed: (base + state.difficulty * 5) * stageMult,
        angle: 0,
        color: "#FF8A00",
        buggy: true, // AI flag: ignore palm walls
      };
      state.enemies.push(enemy);
      state.bossActive = true;
      state.bossSpawned = true;
      return;
    }
  }

  // Outdoor-like: faster; Underground: slower + tighter aisles.
  const outdoorLike =
    state.level === "outdoor" ||
    state.level === "rooftop" ||
    state.level === "snowstorm" ||
    state.level === "neon_rain" ||
    state.level === "beach";
  const base = outdoorLike ? 118 : 102;
  const enemy = {
    x: s.x,
    y: s.y,
    w: 34,
    h: 20,
    baseSpeed: base + state.difficulty * 7 + Math.random() * 12,
    speed: (base + state.difficulty * 7 + Math.random() * 12) * stageMult,
    angle: 0,
    color: COLORS.enemy,
  };
  state.enemies.push(enemy);
}

function spawnPower(state) {
  for (let tries = 0; tries < 30; tries++) {
    const x = irand(60, W - 60);
    const y = irand(60, H - 60);
    const rect = { x, y, w: 16, h: 16 };
    const solids = state.walls.concat(state.obstacles);
    if (!collidesSolid(rect, solids)) {
      const kinds = state.isPro ? ["shield", "speed", "freeze"] : ["shield"];
      const kind = kinds[irand(0, kinds.length - 1)];
      state.powers.push({ x, y, w: 16, h: 16, kind, spawnedAt: state.time });
      return;
    }
  }
}

function applyPower(state, kind) {
  const nowMs = state.time * 1000;
  if (kind === "shield") state.player.invincibleUntil = nowMs + 5000;
  if (kind === "speed") state.player.speedBoostUntil = nowMs + 4000;
  if (kind === "freeze") state.player.freezeUntil = nowMs + 3000;
}

// -------- Initial state --------
export function createGameState(level, difficulty = 1, opts = {}) {
  const buildAndInit = () => {
    const lvl = buildLevel(level);
    const state = {
      level,
      difficulty,
      stage: 1,
      stageChangedAt: 0,
      isPro: !!opts.isPro,
      maxStage: opts.isPro ? Infinity : 3, // FREE tier capped at stage 3
      stageCappedTriggered: false,
      time: 0,
      score: 0,
      scoreMultiplier: Math.max(1, opts.scoreMultiplier || 1),
      lives: 3 + (opts.extraLife ? 1 : 0),
      extraLifeGranted: !!opts.extraLife,
      dailySeed: opts.seed || null,
      wind: null,
      weather: opts.weather || null, // { type: 'rain'|'fog'|'wind', intensity: 0..1 }
      player: {
        x: lvl.playerStart.x,
        y: lvl.playerStart.y,
        w: 14,
        h: 14,
        speed: 155,
        angle: 0,
        invincibleUntil: 0,
        speedBoostUntil: 0,
        freezeUntil: 0,
        skinColor: opts.skinColor || null,
        skinGlow: opts.skinGlow || null,
        handleGem: opts.handleGem || null,
        duelist: !!opts.duelist,
      },
      enemies: [],
      powers: [],
      walls: lvl.walls,
      obstacles: lvl.obstacles,
      exits: lvl.exits,
      enemySpawns: lvl.enemySpawns,
      spawnTimer: 0,
      powerTimer: 3,
      over: false,
      won: false,
      startedAt: performance.now(),
    };

    const initial = level === "underground" ? 3 : 3;
    for (let i = 0; i < initial; i++) spawnEnemy(state);
    return state;
  };

  if (typeof opts.seed === "number") {
    return withSeededMathRandom(opts.seed, buildAndInit);
  }
  return buildAndInit();
}

// -------- Main tick --------
export function updateGame(state, dt, input) {
  if (state.over || state.won) return state;
  state.time += dt;

  const p = state.player;
  // Duelist trail buffer — keep last ~24 positions for a magenta comet-tail
  if (state.player.duelist) {
    if (!p.trail) p.trail = [];
    // Sample every ~30ms (~33fps trail) to keep it smooth without spam
    const last = p.trail[p.trail.length - 1];
    if (!last || (state.time - last.t) > 0.03) {
      p.trail.push({ x: p.x + p.w / 2, y: p.y + p.h / 2, t: state.time });
      if (p.trail.length > 24) p.trail.shift();
    }
  }
  const solidsForPlayer = state.walls.concat(state.obstacles);

  let vx = 0;
  let vy = 0;
  if (input.left) vx -= 1;
  if (input.right) vx += 1;
  if (input.up) vy -= 1;
  if (input.down) vy += 1;
  if (vx !== 0 || vy !== 0) {
    const len = Math.hypot(vx, vy) || 1;
    vx /= len;
    vy /= len;
    p.angle = Math.atan2(vy, vx);
  }
  const boost = state.time * 1000 < p.speedBoostUntil ? 1.7 : 1;
  // Weather 'rain' modifier: 15% slower base movement (wet ground)
  const weatherType = state.weather && state.weather.type;
  const weatherI = state.weather ? state.weather.intensity || 0.6 : 0;
  const rainSlow = weatherType === "rain" ? 1 - 0.15 * weatherI : 1;
  const speed = p.speed * boost * rainSlow;
  let dx;
  let dy;

  // Beach = sand friction (slippery-lite). Snowstorm = full ice.
  const isSlippery = state.level === "snowstorm" || state.level === "beach";
  if (isSlippery) {
    if (!p.vel) p.vel = { x: 0, y: 0 };
    // Beach is less slippery than snow
    const beach = state.level === "beach";
    const accel = beach ? 780 : 620;
    const friction = beach ? 0.985 : 0.9;
    p.vel.x += vx * accel * dt;
    p.vel.y += vy * accel * dt;
    const maxV = speed * (beach ? 1.15 : 1.4);
    const vlen = Math.hypot(p.vel.x, p.vel.y);
    if (vlen > maxV) {
      p.vel.x = (p.vel.x / vlen) * maxV;
      p.vel.y = (p.vel.y / vlen) * maxV;
    }
    const decay = Math.pow(1 - friction, dt);
    p.vel.x *= decay;
    p.vel.y *= decay;
    dx = p.vel.x * dt;
    dy = p.vel.y * dt;
  } else {
    dx = vx * speed * dt;
    dy = vy * speed * dt;
  }

  // Rooftop wind (native to rooftop level)
  if (state.level === "rooftop") {
    const seg = Math.floor(state.time / 5);
    const h = ((seg * 2654435761) >>> 0) % 8;
    const angs = [0, Math.PI / 4, Math.PI / 2, (3 * Math.PI) / 4, Math.PI, (5 * Math.PI) / 4, (3 * Math.PI) / 2, (7 * Math.PI) / 4];
    const wx = Math.cos(angs[h]);
    const wy = Math.sin(angs[h]);
    const t = state.time % 5;
    const gust = Math.sin((t / 5) * Math.PI);
    const windStrength = (28 + Math.min(state.stage, 5) * 5) * gust;
    dx += wx * windStrength * dt;
    dy += wy * windStrength * dt;
    state.wind = { x: wx, y: wy, strength: windStrength };
  }

  // Weather 'wind' modifier (any level): overrides rooftop wind if stronger
  if (weatherType === "wind") {
    const seg = Math.floor(state.time / 4);
    const h = ((seg * 1013904223) >>> 0) % 8;
    const angs = [0, Math.PI / 4, Math.PI / 2, (3 * Math.PI) / 4, Math.PI, (5 * Math.PI) / 4, (3 * Math.PI) / 2, (7 * Math.PI) / 4];
    const wx = Math.cos(angs[h]);
    const wy = Math.sin(angs[h]);
    const t = state.time % 4;
    const gust = Math.sin((t / 4) * Math.PI);
    const windStrength = (36 + Math.min(state.stage, 5) * 6) * gust * weatherI;
    dx += wx * windStrength * dt;
    dy += wy * windStrength * dt;
    state.wind = { x: wx, y: wy, strength: windStrength };
  }

  const moved = moveRect({ x: p.x, y: p.y, w: p.w, h: p.h }, dx, dy, solidsForPlayer);
  if (isSlippery && p.vel) {
    if (moved.x === p.x && dx !== 0) p.vel.x = 0;
    if (moved.y === p.y && dy !== 0) p.vel.y = 0;
  }
  p.x = moved.x;
  p.y = moved.y;

  // Stage progression
  const rawStage = stageFromScore(state.score);
  const newStage = Math.min(rawStage, state.maxStage || Infinity);
  if (newStage > state.stage) {
    const prev = state.stage;
    state.stage = newStage;
    state.stageChangedAt = state.time;
    state.score += Math.floor(100 * (newStage - prev) * state.scoreMultiplier);
    const mult = stageSpeedMult(newStage);
    for (const e of state.enemies) {
      e.speed = (e.baseSpeed || e.speed) * mult;
    }
    const extra = Math.min(2, newStage - prev);
    for (let i = 0; i < extra; i++) spawnEnemy(state);
  }
  if (rawStage > state.maxStage && !state.stageCappedTriggered) {
    state.stageCappedTriggered = true;
  }

  const nowMs = state.time * 1000;
  const spawnEvery = stageSpawnInterval(state.stage, state.time);
  state.spawnTimer += dt;
  const enemyCap = stageEnemyCap(state, state.stage);
  if (state.spawnTimer >= spawnEvery && state.enemies.length < enemyCap) {
    state.spawnTimer = 0;
    spawnEnemy(state);
  }

  state.powerTimer -= dt;
  if (state.powerTimer <= 0) {
    if (state.powers.length < 2) spawnPower(state);
    state.powerTimer = rand(8, 14);
  }

  // Enemies chase (except highway lane-locked traffic)
  const enemySolids = state.walls.concat(state.obstacles);
  // Buggies (beach boss) ignore palm-tree walls — precompute their solids list once.
  const enemySolidsNoPalms = state.walls.some((w) => w.palm)
    ? state.walls.filter((w) => !w.palm).concat(state.obstacles)
    : enemySolids;
  const frozen = nowMs < p.freezeUntil;
  const isHighway = state.level === "highway";
  for (let i = state.enemies.length - 1; i >= 0; i--) {
    const e = state.enemies[i];
    if (frozen) continue;
    if (isHighway && e.highwayDir) {
      e.x += e.highwayDir * e.speed * dt;
      if ((e.highwayDir === 1 && e.x > W + 50) || (e.highwayDir === -1 && e.x + e.w < -50)) {
        state.enemies.splice(i, 1);
      }
      continue;
    }
    const dxTo = p.x - e.x;
    const dyTo = p.y - e.y;
    const dist = Math.hypot(dxTo, dyTo) || 1;
    const nx = dxTo / dist;
    const ny = dyTo / dist;
    e.angle = Math.atan2(dyTo, dxTo);
    const step = e.speed * dt;
    const solids = e.buggy ? enemySolidsNoPalms : enemySolids;
    const mv = moveRect({ x: e.x, y: e.y, w: e.w, h: e.h }, nx * step, ny * step, solids);
    if (mv.x === e.x && mv.y === e.y) {
      const jitter = Math.random() < 0.5 ? 1 : -1;
      const mv2 = moveRect({ x: e.x, y: e.y, w: e.w, h: e.h }, -ny * step * jitter, nx * step * jitter, solids);
      e.x = mv2.x;
      e.y = mv2.y;
    } else {
      e.x = mv.x;
      e.y = mv.y;
    }
  }

  // Powers
  for (let i = state.powers.length - 1; i >= 0; i--) {
    const pw = state.powers[i];
    if (aabb(p, pw)) {
      applyPower(state, pw.kind);
      state.powers.splice(i, 1);
      state.score += Math.floor(25 * state.scoreMultiplier);
    }
  }

  // Enemy collision
  const invincible = nowMs < p.invincibleUntil;
  if (!invincible) {
    for (const e of state.enemies) {
      if (aabb(p, e)) {
        state.lives -= 1;
        p.invincibleUntil = nowMs + 1500;
        if (state.lives <= 0) {
          state.over = true;
        }
        break;
      }
    }
  }

  if (isInExit(p, state.exits)) {
    const winBonus = Math.max(0, 500 - Math.floor(state.time * 5)) + state.lives * 200;
    state.score += Math.floor(winBonus * state.scoreMultiplier);
    state.won = true;
  }

  state.scoreFrac = (state.scoreFrac || 0) + dt * 30 * state.scoreMultiplier;
  if (state.scoreFrac >= 1) {
    const inc = Math.floor(state.scoreFrac);
    state.score += inc;
    state.scoreFrac -= inc;
  }

  return state;
}
